import { gql } from '@apollo/client';
import React from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import type { ButtonProps } from '../components/Button';
import Button from '../components/Button';
import { MenuItem } from '../components/Menu';
import Tooltip from '../components/Tooltip';
import type { PostDraftBarPublishButton_PostFragment } from '../graphql/generated/graphqlTypes';
import {
  PostLinkAccess,
  PostType,
  UserType,
} from '../graphql/generated/graphqlTypes';
import publishIcon from '../icons/publish.svg';
import PostStateBarOverflowMenu from './PostStateBarOverflowMenu';
import { createPostPath } from '../utils/url/postPath';
import styles from './PostDraftBarPublishButton.module.scss';
import useCurrentPostTopicIds from './useCurrentPostTopicIds';
import usePublishPost from './usePublishPost';

const POST_DRAFT_BAR_PUBLISH_BUTTON_POST = gql`
  fragment PostDraftBarPublishButton_post on Post {
    id
    linkAccess
    type
  }
`;

interface PostPublishButtonProps extends ButtonProps {
  post: PostDraftBarPublishButton_PostFragment;
}

const PostDraftBarPublishButton = ({
  post,
  ...buttonProps
}: PostPublishButtonProps) => {
  const postTopicIds = useCurrentPostTopicIds();
  const isGuest = useAppSelector(
    (state) => state.session.user.type === UserType.Guest,
  );

  const publishPost = usePublishPost(post.id);

  const hasTopic = postTopicIds.length > 0;
  const isDisabled = isGuest && !hasTopic;
  const tooltip = isDisabled
    ? 'Guests must publish posts\nin at least one topic'
    : null;

  return (
    <div className={styles.container}>
      {post.linkAccess === PostLinkAccess.Internal ? (
        <>
          {post.type === PostType.Template && (
            <PostStateBarOverflowMenu>
              <MenuItem
                disabled={isDisabled}
                tooltip={tooltip}
                to={createPostPath({ templateId: post.id })}
                icon={publishIcon}
              >
                Create post
              </MenuItem>
            </PostStateBarOverflowMenu>
          )}
          <Tooltip trigger="hover" placement="bottom" tooltip={tooltip}>
            <Button
              disabled={isDisabled}
              variant="primary"
              onClick={() => publishPost(true)}
              {...buttonProps}
            >
              Publish
            </Button>
          </Tooltip>
        </>
      ) : (
        <>
          <PostStateBarOverflowMenu>
            <MenuItem
              disabled={isDisabled}
              tooltip={tooltip}
              onClick={() => publishPost(true)}
              icon={publishIcon}
            >
              Publish
            </MenuItem>
          </PostStateBarOverflowMenu>
          {post.type === PostType.Template && (
            <Tooltip trigger="hover" placement="bottom" tooltip={tooltip}>
              <Button
                disabled={isDisabled}
                variant="primary"
                to={createPostPath({ templateId: post.id })}
                {...buttonProps}
              >
                Create post
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
};

PostDraftBarPublishButton.fragments = {
  post: POST_DRAFT_BAR_PUBLISH_BUTTON_POST,
};

export default PostDraftBarPublishButton;
